/**
 * Helpers for the dev-only wallet impersonation flow used by `DevDebugHeader`.
 *
 * Production builds short-circuit immediately: there is no impersonation in
 * non-dev `NODE_ENV` and all helpers return safe defaults.
 */

export const DEV_IMPERSONATION_STORAGE_KEY = 'dev:impersonatedWallet';

const IS_DEV = process.env.NODE_ENV === 'development';

const readImpersonatedWallet = (): string | null => {
  if (!IS_DEV || typeof window === 'undefined') return null;
  try {
    // sessionStorage (not localStorage) so an impersonation session does not
    // silently survive a browser restart and suppress the chain-network gate
    // in a future tab.
    return window.sessionStorage.getItem(DEV_IMPERSONATION_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Returns true if dev impersonation is currently active. Pure presence check —
 * does not validate the address format.
 */
export const isDevImpersonationActive = (): boolean => Boolean(readImpersonatedWallet());

/**
 * Returns the impersonated wallet address (lowercase) or null when not set.
 */
export const getImpersonatedWallet = (): string | null => {
  const value = readImpersonatedWallet();
  return value ? value.toLowerCase() : null;
};

/**
 * Returns true if the given address matches the active impersonated wallet.
 * Use this to gate side effects (profile fetches, notifications, etc.) that
 * only make sense for a real signed-in session.
 */
export const isImpersonatedWalletAddress = (address: string | null | undefined): boolean => {
  if (!address) return false;
  const impersonated = getImpersonatedWallet();
  if (!impersonated) return false;
  return address.toLowerCase() === impersonated;
};
